// navigation-completion Stage C — reusable navigation exception request/review
// control. Designed to be dropped into the Settings review area now and the
// per-document navigation panel later; it is scope-driven so the same component
// serves both.
//
// Honesty the UI must preserve:
//   • An exception is NOT a pass. An exempted failure is rendered as
//     "Failed — exempted (authorised departure)", never green/passing.
//   • The EXACT artifact hash and scope (document / criterion / profile) are
//     shown so a reviewer sees precisely what is being exempted.
//   • The artifact hash the exception binds to comes from server metadata; the
//     request form only CONFIRMS it (expectedArtifactHash), it cannot choose it.

import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { formatApiError } from '@/lib/errors';
import {
  navigationExceptions,
  type NavigationExceptionWire,
  type RequestExceptionBody,
} from '@/api/navigationPolicy';

const boxStyle: CSSProperties = { border: '1px solid var(--border, #ddd)', borderRadius: 6, padding: 10, fontSize: 13 };
const subtleStyle: CSSProperties = { color: 'var(--text-muted, #666)', fontSize: 12 };
const monoStyle: CSSProperties = { fontFamily: 'var(--mono, monospace)', fontSize: 11, wordBreak: 'break-all' };
const errorStyle: CSSProperties = { color: 'var(--red, #b00020)', fontSize: 13, marginTop: 6 };

/** A status chip whose colour never implies "pass" for an exempted failure. */
function statusChip(ex: NavigationExceptionWire): { label: string; bg: string; fg: string } {
  if (ex.status === 'revoked') return { label: 'Revoked', bg: 'var(--border, #e5e5e5)', fg: '#555' };
  if (ex.status === 'pending') return { label: 'Pending approval', bg: 'var(--amber-bg, #fff8e1)', fg: '#7a5a00' };
  // Approved: an exemption of a FAILURE. Never green.
  if (ex.evaluation && ex.evaluation.valid) {
    return { label: 'Failed — exempted (authorised departure)', bg: 'var(--amber, #e0b000)', fg: '#1a1a1a' };
  }
  return { label: `Exemption not in effect${ex.evaluation?.reason ? ` (${ex.evaluation.reason})` : ''}`, bg: 'var(--red-bg, #fdecee)', fg: 'var(--red, #b00020)' };
}

export interface NavigationExceptionScope {
  readonly documentId?: string;
  readonly criterionId?: string;
  readonly profile?: string;
}

export interface NavigationExceptionRequestContext {
  readonly documentId: string;
  readonly criterionId: string;
  readonly profile: string;
  /** The current server-side artifact hash (shown and confirmed on request). */
  readonly artifactSha256: string;
  readonly expectedRevision?: number;
  readonly expectedPolicyVersion?: number;
  readonly expectedTargetScopeFingerprint?: string;
  readonly targetScopeDescription?: string;
}

export interface NavigationExceptionControlsProps {
  readonly submissionId: string;
  /** Filter the listed exceptions to a scope (e.g. one finding). */
  readonly scope?: NavigationExceptionScope;
  /** When set, a request form for this exact finding is shown. */
  readonly requestContext?: NavigationExceptionRequestContext;
  readonly canRequest?: boolean;
  readonly canApprove?: boolean;
  readonly canRevoke?: boolean;
  /**
   * Fired after a successful request / approve / revoke so a host surface can
   * reload whatever depends on the exemption disposition (e.g. re-run the
   * document assessment). An exempted failure is still a failure; the host
   * decides what to refresh.
   */
  readonly onMutation?: () => void;
}

function inScope(ex: NavigationExceptionWire, scope?: NavigationExceptionScope): boolean {
  if (!scope) return true;
  if (scope.documentId && ex.documentId.toLowerCase() !== scope.documentId.toLowerCase()) return false;
  if (scope.criterionId && ex.criterionId !== scope.criterionId) return false;
  if (scope.profile && ex.profile !== scope.profile) return false;
  return true;
}

export function NavigationExceptionControls(props: NavigationExceptionControlsProps): ReactElement {
  const { submissionId, scope, requestContext, canRequest, canApprove, canRevoke, onMutation } = props;
  const [exceptions, setExceptions] = useState<readonly NavigationExceptionWire[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  // Optional evidence / expiry — the request API already accepts both (design
  // §4E). They stay OPTIONAL: an empty value is simply omitted, never a fabricated
  // requirement, and separation of duties / approval authority stay server-side.
  const [evidence, setEvidence] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const load = useCallback(() => {
    setError(null);
    navigationExceptions
      .list(submissionId)
      .then((res) => setExceptions(res.exceptions))
      .catch((e: unknown) => setError(formatApiError(e, 'Failed to load exceptions')));
  }, [submissionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRequest = useCallback(async () => {
    if (!requestContext) return;
    setError(null);
    if (reason.trim().length === 0) {
      setError('A reason is required to request an exception.');
      return;
    }
    // Expiry must be a real instant when supplied — never send an unparseable
    // string the server would store as a broken expiry.
    let expiresIso: string | undefined;
    if (expiresAt.trim().length > 0) {
      const parsed = new Date(expiresAt);
      if (Number.isNaN(parsed.getTime())) {
        setError('The expiry date is not a valid date.');
        return;
      }
      expiresIso = parsed.toISOString();
    }
    const evidenceRefs = evidence
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const body: RequestExceptionBody = {
      documentId: requestContext.documentId,
      expectedArtifactHash: requestContext.artifactSha256,
      criterionId: requestContext.criterionId,
      profile: requestContext.profile,
      reason: reason.trim(),
      ...(requestContext.expectedRevision !== undefined ? { expectedRevision: requestContext.expectedRevision } : {}),
      ...(requestContext.expectedPolicyVersion !== undefined ? { expectedPolicyVersion: requestContext.expectedPolicyVersion } : {}),
      ...(requestContext.expectedTargetScopeFingerprint !== undefined ? { expectedTargetScopeFingerprint: requestContext.expectedTargetScopeFingerprint } : {}),
      ...(evidenceRefs.length > 0 ? { evidence: evidenceRefs } : {}),
      ...(expiresIso !== undefined ? { expiresAt: expiresIso } : {}),
    };
    setBusy(true);
    try {
      await navigationExceptions.request(submissionId, body);
      setReason('');
      setEvidence('');
      setExpiresAt('');
      load();
      onMutation?.();
    } catch (e) {
      setError(formatApiError(e, 'Failed to request exception'));
    } finally {
      setBusy(false);
    }
  }, [requestContext, reason, evidence, expiresAt, submissionId, load, onMutation]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, failMsg: string) => {
      setError(null);
      setBusy(true);
      try {
        await fn();
        load();
        onMutation?.();
      } catch (e) {
        setError(formatApiError(e, failMsg));
      } finally {
        setBusy(false);
      }
    },
    [load, onMutation],
  );

  const visible = (exceptions ?? []).filter((ex) => inScope(ex, scope));

  return (
    <div style={boxStyle} data-testid="navigation-exception-controls">
      <strong>Navigation exceptions</strong>
      {requestContext && (
        <div style={{ marginTop: 8 }} data-testid="nav-exception-request-form">
          <div style={subtleStyle}>
            Exempting {requestContext.criterionId} · {requestContext.profile} for document{' '}
            <span style={monoStyle}>{requestContext.documentId}</span>
          </div>
          <div style={subtleStyle}>
            Artifact (server bytes): <span style={monoStyle} data-testid="nav-exception-artifact">{requestContext.artifactSha256}</span>
          </div>
          {requestContext.targetScopeDescription && <div style={subtleStyle}>{requestContext.targetScopeDescription}</div>}
          <textarea
            aria-label="exception-reason"
            placeholder="Documented reason for this authorised departure"
            value={reason}
            disabled={!canRequest || busy}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', marginTop: 6, minHeight: 48 }}
          />
          <textarea
            aria-label="exception-evidence"
            data-testid="nav-exception-evidence"
            placeholder="Optional: supporting evidence references, one per line (ticket id, record hash…)"
            value={evidence}
            disabled={!canRequest || busy}
            onChange={(e) => setEvidence(e.target.value)}
            style={{ width: '100%', marginTop: 6, minHeight: 36 }}
          />
          <div style={{ marginTop: 6 }}>
            <label style={subtleStyle} htmlFor="nav-exception-expiry">
              Optional expiry (the departure is reassessed at release and lapses after this date):{' '}
            </label>
            <input
              id="nav-exception-expiry"
              type="date"
              aria-label="exception-expiry"
              data-testid="nav-exception-expiry"
              value={expiresAt}
              disabled={!canRequest || busy}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
          <button
            type="button"
            data-testid="nav-exception-request"
            aria-label="request-exception"
            disabled={!canRequest || busy}
            onClick={handleRequest}
          >
            Request exception
          </button>
          {!canRequest && <span style={subtleStyle}> (requires Documents:write)</span>}
        </div>
      )}

      {error && (
        <div role="alert" style={errorStyle} data-testid="nav-exception-error">
          {error}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        {exceptions === null ? (
          <p>Loading…</p>
        ) : visible.length === 0 ? (
          <p style={subtleStyle} data-testid="nav-exception-empty">
            No exceptions recorded for this scope.
          </p>
        ) : (
          visible.map((ex) => {
            const chip = statusChip(ex);
            return (
              <div
                key={ex.id}
                data-testid={`nav-exception-${ex.id}`}
                style={{ borderTop: '1px solid var(--border, #eee)', padding: '6px 0' }}
              >
                <div>
                  <span
                    data-testid={`nav-exception-status-${ex.id}`}
                    style={{ background: chip.bg, color: chip.fg, padding: '1px 6px', borderRadius: 3, fontSize: 11, fontWeight: 700 }}
                  >
                    {chip.label}
                  </span>{' '}
                  {ex.criterionId} · {ex.profile}
                </div>
                <div style={subtleStyle}>Reason: {ex.reason}</div>
                <div style={subtleStyle}>
                  Artifact: <span style={monoStyle}>{ex.artifactSha256}</span> · policy v{ex.policyVersion}
                </div>
                <div style={subtleStyle}>
                  Requested by {ex.requestedBy}
                  {ex.approvedBy ? ` · approved by ${ex.approvedBy}` : ' · not yet approved'}
                </div>
                <div style={{ marginTop: 4 }}>
                  {ex.status === 'pending' && canApprove && (
                    <button
                      type="button"
                      data-testid={`nav-exception-approve-${ex.id}`}
                      disabled={busy}
                      onClick={() => act(() => navigationExceptions.approve(submissionId, ex.id, ex.version), 'Failed to approve')}
                    >
                      Approve
                    </button>
                  )}
                  {ex.status !== 'revoked' && canRevoke && (
                    <button
                      type="button"
                      data-testid={`nav-exception-revoke-${ex.id}`}
                      disabled={busy}
                      onClick={() => act(() => navigationExceptions.revoke(submissionId, ex.id, ex.version), 'Failed to revoke')}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
